// We want to allow people without AC on their profile to launch AC forms, as we
// always ask them to re-confirm on the form itself. These are some sensible defaults
// for the list of accreds which can do this. Technically a social worker can be an AC
// but it is rare, so we require them to have the AC accreditation explicitly on their profile.
export const approvedClinicianAccreds = [
    "doctor",
    "nurse",
    "hcpc",
    "approved-clinician",
];
export function validateAccreditation(accreditations) {
    const errors = [];
    const types = new Set(accreditations.map((t) => t.type));
    if (types.size !== accreditations.length) {
        errors.push("Duplicate accreditation type in accreditations");
    }
    if (accreditations.find((acc) => acc.type === "amhp")) {
        if (!accreditations.find((accreditation) => accreditation.type === "social-worker" ||
            accreditation.type === "hcpc" ||
            accreditation.type === "nurse")) {
            errors.push("AMHPs must have either a HCPC, Nurse, or Social Worker accreditation");
        }
    }
    if (accreditations.find((acc) => acc.type === "approved-clinician")) {
        if (!accreditations.find((accreditation) => accreditation.type === "social-worker" ||
            accreditation.type === "hcpc" ||
            accreditation.type === "nurse" ||
            accreditation.type === "doctor")) {
            errors.push("Approved Clinicians must be either a Doctor, HCPC, Nurse, or Social Worker");
        }
    }
    if (accreditations.find((acc) => acc.type === "section-12")) {
        if (!accreditations.find((accreditation) => accreditation.type === "doctor")) {
            errors.push("S12 approved clinicians must be a Doctor");
        }
    }
    return errors;
}
export function isAmhpAccreditation(accreditation) {
    return accreditation.type === "amhp";
}
export function isDoctorAccreditation(accreditation) {
    return accreditation.type === "doctor";
}
export function isSocialWorkerAccreditation(accreditation) {
    return accreditation.type === "social-worker";
}
export function isHcpcAccreditation(accreditation) {
    return accreditation.type === "hcpc";
}
export function isSection12Accreditation(accreditation) {
    return accreditation.type === "section-12";
}
export function replaceSection12Accreditation(accreditations, newS12) {
    return [
        ...accreditations.filter((a) => !isSection12Accreditation(a)),
        newS12,
    ];
}
export function isNurseAccreditation(accreditation) {
    return accreditation.type === "nurse";
}
export function isApprovedClinicianAccreditation(accreditation) {
    return accreditation.type === "approved-clinician";
}
export function replaceApprovedClinicianAccreditation(accreditations, newAc) {
    return [
        ...accreditations.filter((a) => !isApprovedClinicianAccreditation(a)),
        newAc,
    ];
}
export function isAcdFacilitatorAccreditation(accreditation) {
    return accreditation.type === "acd-facilitator";
}
// Accreditation types treated as "clinical" for the purposes of viewing
// detailed clinical data sourced from NHS Spine (e.g. RAF condition/createdBy
// detail). Deliberately excludes `acd-facilitator`.
export const clinicalAccreds = [
    "doctor",
    "nurse",
    "hcpc",
    "amhp",
    "section-12",
    "approved-clinician",
    "social-worker",
];
export function hasClinicalAccreditation(accreditations) {
    return accreditations.some((a) => clinicalAccreds.includes(a.type));
}
export const isAdminEmail = (email) => email.endsWith("@thalamos.co.uk");
export const isAdminUser = (user) => user && user.isAdminSession;
export const isDemographicsManagerUser = (user) => user &&
    user.sessionContext?.role ===
        "demographics-manager";
export const isManagerUser = (user) => user && user?.sessionContext?.role === "manager";
export const isNHSDashboardViewerUser = (user) => user && user?.sessionContext?.organisationRole === "nhs/dashboard";
export const isSupersetDashboardViewerUser = (user) => user && user?.sessionContext?.organisationRole === "superset/dashboard";
export const hasConfiguredSupersetDashboards = (user) => (user?.sessionOrganisationConfiguration?.embedDashboards?.length ?? 0) > 0;
export const canViewSupersetDashboards = (user) => isSupersetDashboardViewerUser(user) || hasConfiguredSupersetDashboards(user);
export const canViewMhaStatusDashboard = (user) => user?.sessionContext?.teamType === "mha" &&
    !!user?.sessionOrganisationConfiguration?.patientStateEnabled;
export const isPatientListEnabled = (user) => !!user?.sessionOrganisationConfiguration?.patientListEnabled;
/**
 * Section 132 information is owed for a section held as a patient's MHA
 * status, so recording it needs patient state as well.
 */
export const isS132RecordingEnabledFor = (configuration) => !!configuration?.s132RecordingEnabled && !!configuration.patientStateEnabled;
export const isS132RecordingEnabled = (user) => isS132RecordingEnabledFor(user?.sessionOrganisationConfiguration);
/**
 * A patient list belongs to one user within one data controller. It is
 * deliberately offered only in team sessions, though an organisation session has
 * a data controller too, and deliberately open to every `teamType`. Not narrowed
 * to patientStateEnabled: an entry stays on the list whether or not an MHA
 * status is recorded for its patient.
 */
export const canViewPatientList = (user) => isPatientListEnabled(user) && user?.sessionContext?.type === "team";
export const isTeamManagerUser = (user) => user && user?.sessionContext?.type === "team" && isManagerUser(user);
export const isGuestUserSession = (user) => user?.sessionContext?.teamType === "guest";
export const isIndependentUserSession = (user) => user?.sessionContext?.teamType === "independent";
export const isSoadUserSession = (user) => user?.sessionContext?.teamType === "soad";
export const isCqcUserSession = (user) => user?.sessionContext?.teamType === "cqc";
export const isMcaUserSession = (user) => user?.sessionContext?.teamType === "mca";
/**
 * MCA teams work from a care home register rather than a caseload, so their
 * landing page and menu are the register — see HomePage and menuData.
 */
export const canViewCareHomeRegister = (user) => isMcaUserSession(user) &&
    !!user?.sessionOrganisationConfiguration?.mcaOversightEnabled;
export const canManageCareHomeRegister = (user) => canViewCareHomeRegister(user) && user?.sessionContext?.role === "member";
export const isPaymentReviewerSession = (user) => user?.sessionContext?.teamType === "section-12-payment-reviewers";
export const hasDoctorGmcNumber = (user) => user?.accreditations?.some((a) => a.type === "doctor" && !!a.gmcNumber) ??
    false;
export const hasGuestContext = (user) => user
    ? user.memberships.some((m) => m.type === "team" &&
        m.teamType === "guest" &&
        m.teamStatus === "active")
    : false;
export const isGuestUser = (user) => {
    const membership = user?.memberships?.[0];
    return !!(user &&
        user.authenticationMethods.length === 0 &&
        user.memberships.length === 1 &&
        membership !== undefined &&
        membership.type === "team" &&
        membership.teamStatus === "active" &&
        membership.teamType === "guest");
};
// The user's active guest team membership, if any. Unlike isGuestUser this
// tolerates the user holding other memberships (e.g. a dashboard organisation),
// so the guest team can be resolved for a user who is a guest for one incident
// while retaining separate eMHA access.
export const getActiveGuestTeamMembership = (user) => user?.memberships.find((m) => m.type === "team" && m.teamType === "guest" && m.teamStatus === "active");
// True when the user belongs to any active non-guest team. That access — its
// forms and PID — is reached through an identity-provider login, never the
// guest email-code flow. Organisation (e.g. dashboard) memberships are not
// teams and so do not count. Typed on `memberships` alone rather than a full
// user type, so both fully- and partially-resolved user shapes qualify.
export const hasActiveNonGuestTeamMembership = (user) => user?.memberships.some((m) => m.type === "team" && m.teamStatus === "active" && m.teamType !== "guest") ?? false;
// Guest email-code login is available only to accounts whose sole team access
// is a guest team; they may additionally hold organisation (dashboard)
// memberships. Any active non-guest team means the user has an identity-provider
// login and must never authenticate through the guest flow — even if a guest
// team has been attached to them, e.g. by a share invite.
export const isGuestLoginEligible = (user) => hasGuestContext(user) && !hasActiveNonGuestTeamMembership(user);
//# sourceMappingURL=user.js.map